import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MdDialogRef, MD_DIALOG_DATA, MdDialog, MdDialogConfig } from '@angular/material';

import { TransactionMonitoringModel } from '../entity-transaction-monitoring.model';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { EntityDetailsService } from 'app/entities/details/entity-details.service';
import { EntityTransactionProfileService } from 'app/entities/transaction-profile/entity-transaction-profile.service';
import { TransactionRule, TxRuleData, ReviewStatus } from 'app/entities/transaction-profile/right.model';
import { EntityTransactionMonitoringService } from '../entity-transaction-monitoring.service';
import { FinanceService } from 'app/finance/finance.service';
import { BankAccount } from 'app/shared/models/bank-account.model';
import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { AddTransactionRuleComponent } from 'app/entities/transaction-profile/transaction-rule/add-transaction-rule/add-transaction-rule.component';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmationdialog/confirmation-dialog.component';
import { RuleHistoryDialogComponent } from '../rule-history-dialog/rule-history-dialog.component';
import { formatDisplayDate } from 'app/shared/utils/date-format.util';

interface MaxAmountTransaction {
  txnId: string;
  txnDate: string;
  amount: string;
  currency: string;
  counterPartyName: string;
  description: string;
  accountNumber: string;
  status: string;
}

@Component({
  selector: 'test-profile-wizard',
  templateUrl: './test-profile-wizard.component.html',
  styleUrls: ['./test-profile-wizard.component.css']
})
export class TestProfileWizardComponent implements OnInit {
  transactionRecord: TransactionMonitoringModel;
  entityIdDetails: EntityIdDetails;

  // Profile Selection
  transactionProfiles: TransactionRule[] = [];
  filteredProfiles: TransactionRule[] = [];
  displayedProfiles: TransactionRule[] = [];
  selectedProfile: TransactionRule = null;
  showAllProfiles = false;
  searchQuery: string = '';
  testResult: {
    matches: boolean;
    message: string;
    passedCriteria?: string[];
    failedCriteria?: string[];
    maxAmountTransactions?: MaxAmountTransaction[];
    maxAmountTotal?: string;
    maxAmountExcludedDuplicates?: MaxAmountTransaction[];
  } = null;
  isTesting = false;
  isLoadingProfiles = false;
  transactionStatus: ApplicationValue[] = [];
  
  // Expose ReviewStatus enum to template
  ReviewStatus = ReviewStatus;

  constructor(
    public dialogRef: MdDialogRef<TestProfileWizardComponent>,
    @Optional() @Inject(MD_DIALOG_DATA) public data: { transactionRecord: TransactionMonitoringModel },
    private entityDetailsService: EntityDetailsService,
    private transactionProfileService: EntityTransactionProfileService,
    private transactionMonitoringService: EntityTransactionMonitoringService,
    private financeService: FinanceService,
    private warmUpService: WarmUpService,
    private dialog: MdDialog
  ) {
    if (data && data.transactionRecord) {
      this.transactionRecord = data.transactionRecord;
    }
  }

  ngOnInit() {
    this.loadEntityDetails();
    this.loadTransactionStatus();
  }

  loadTransactionStatus() {
    const setTransactionStatus = (values: ApplicationValue[]) => {
      this.transactionStatus = values;
    };
    this.warmUpService.getApplicationValueList(ApplicationTypeConstants.TRANSACTION_STATUS, setTransactionStatus);
  }

  getStatusName(statusId: number): string {
    if (!statusId || !this.transactionStatus || this.transactionStatus.length === 0) {
      return 'N/A';
    }
    const match = this.transactionStatus.find(status => status.id === statusId);
    return match ? match.name : 'N/A';
  }

  formatDate(date: Date): string {
    if (!date) return 'N/A';
    return formatDisplayDate(date) || 'N/A';
  }

  getReviewStatusName(reviewStatus: ReviewStatus | number | string): string {
    if (reviewStatus === null || reviewStatus === undefined) {
      return 'N/A';
    }
    // Handle string values from backend (most common case)
    if (typeof reviewStatus === 'string') {
      return reviewStatus; // Backend sends "DRAFT", "FINAL", etc. as strings
    }
    // Handle enum values and numeric values
    if (reviewStatus === ReviewStatus.DRAFT || reviewStatus === 0) {
      return 'DRAFT';
    } else if (reviewStatus === ReviewStatus.FINAL || reviewStatus === 1) {
      return 'FINAL';
    } else if (reviewStatus === ReviewStatus.REVIEW || reviewStatus === 2) {
      return 'REVIEW';
    }
    return 'N/A';
  }

  getDisplayDate(profile: TransactionRule): Date {
    return profile.lastModifiedOn || profile.createdOn;
  }

  loadEntityDetails() {
    this.entityIdDetails = this.entityDetailsService.getEntityIdDetails();
    if (this.entityIdDetails) {
      this.loadProfiles();
    }
  }

  loadProfiles() {
    if (this.entityIdDetails && this.entityIdDetails.clientId) {
      this.isLoadingProfiles = true;
      this.transactionProfileService.getTransactionRulesByClientId(this.entityIdDetails.clientId)
        .subscribe(profiles => {
          // Deduplicate rules by ID only (backend already handles versioning)
          // Different rules can legitimately have the same name, so we should NOT deduplicate by name
          const profilesMap = new Map<number, TransactionRule>();
          (profiles || []).forEach(profile => {
            if (profile.id) {
              const existing = profilesMap.get(profile.id);
              if (!existing) {
                profilesMap.set(profile.id, profile);
              }
            }
          });
          
          // Use all unique rules by ID (backend already returns only latest versions)
          this.transactionProfiles = Array.from(profilesMap.values());
          this.filterProfiles();
          this.isLoadingProfiles = false;
        }, error => {
          console.error('Error loading transaction profiles:', error);
          this.transactionProfiles = [];
          this.filteredProfiles = [];
          this.displayedProfiles = [];
          this.isLoadingProfiles = false;
        });
    }
  }

  /**
   * Normalizes a name for comparison, matching the Java NameMatcher.normalize() logic.
   * Removes legal entity suffixes and normalizes abbreviations.
   */
  private normalizeName(name: string): string {
    if (!name || name.trim() === '') {
      return '';
    }

    let normalized = name.toLowerCase();

    // Normalize common abbreviations with periods to their non-period versions
    // Romanian/Italian: S.R.L. / SRL
    normalized = normalized.replace(/\b(s\.r\.l\.|s\.r\.l)\b/g, ' srl ');

    // US/International: L.L.C. / LLC, I.N.C. / INC, C.O. / CO, L.T.D. / LTD
    normalized = normalized.replace(/\b(l\.l\.c\.|l\.l\.c)\b/g, ' llc ');
    normalized = normalized.replace(/\b(i\.n\.c\.|i\.n\.c)\b/g, ' inc ');
    normalized = normalized.replace(/\b(c\.o\.|c\.o)\b/g, ' co ');
    normalized = normalized.replace(/\b(l\.t\.d\.|l\.t\.d)\b/g, ' ltd ');

    // Dutch: B.V. / BV, C.V. / CV, N.V. / NV
    normalized = normalized.replace(/\b(b\.v\.|b\.v)\b/g, ' bv ');
    normalized = normalized.replace(/\b(c\.v\.|c\.v)\b/g, ' cv ');
    normalized = normalized.replace(/\b(n\.v\.|n\.v)\b/g, ' nv ');

    // German/Austrian: G.m.b.H. / GmbH, A.G. / AG, K.G. / KG, O.H.G. / OHG, U.G. / UG
    normalized = normalized.replace(/\b(g\.m\.b\.h\.|g\.m\.b\.h)\b/g, ' gmbh ');
    normalized = normalized.replace(/\b(a\.g\.|a\.g)\b/g, ' ag ');
    normalized = normalized.replace(/\b(k\.g\.|k\.g)\b/g, ' kg ');
    normalized = normalized.replace(/\b(o\.h\.g\.|o\.h\.g)\b/g, ' ohg ');
    normalized = normalized.replace(/\b(u\.g\.|u\.g)\b/g, ' ug ');

    // French: S.A. / SA, S.A.R.L. / SARL, S.A.S. / SAS, S.N.C. / SNC
    normalized = normalized.replace(/\b(s\.a\.|s\.a)\b/g, ' sa ');
    normalized = normalized.replace(/\b(s\.a\.r\.l\.|s\.a\.r\.l)\b/g, ' sarl ');
    normalized = normalized.replace(/\b(s\.a\.s\.|s\.a\.s)\b/g, ' sas ');
    normalized = normalized.replace(/\b(s\.n\.c\.|s\.n\.c)\b/g, ' snc ');

    // Italian: S.P.A. / SPA
    normalized = normalized.replace(/\b(s\.p\.a\.|s\.p\.a)\b/g, ' spa ');

    // Spanish: S.L. / SL
    normalized = normalized.replace(/\b(s\.l\.|s\.l)\b/g, ' sl ');

    // UK/Commonwealth: P.L.C. / PLC, L.L.P. / LLP, L.P. / LP
    normalized = normalized.replace(/\b(p\.l\.c\.|p\.l\.c)\b/g, ' plc ');
    normalized = normalized.replace(/\b(l\.l\.p\.|l\.l\.p)\b/g, ' llp ');
    normalized = normalized.replace(/\b(l\.p\.|l\.p)\b/g, ' lp ');

    // Nordic: A.S. / AS, O.Y. / OY, A.B. / AB
    normalized = normalized.replace(/\b(a\.s\.|a\.s)\b/g, ' as ');
    normalized = normalized.replace(/\b(o\.y\.|o\.y)\b/g, ' oy ');
    normalized = normalized.replace(/\b(a\.b\.|a\.b)\b/g, ' ab ');

    // Asian: P.T. / PT, P.T.E. / PTE, P.V.T. / PVT
    normalized = normalized.replace(/\b(p\.t\.|p\.t)\b/g, ' pt ');
    normalized = normalized.replace(/\b(p\.t\.e\.|p\.t\.e)\b/g, ' pte ');
    normalized = normalized.replace(/\b(p\.v\.t\.|p\.v\.t)\b/g, ' pvt ');

    // South African: (Pty) / Pty
    normalized = normalized.replace(/\b\(?pty\)?\b/g, ' pty ');

    // Remove all legal entity suffixes
    normalized = normalized.replace(/\b(corp|corporation|limited|company|ltd|pty|sl|srl|llc|inc|co|bv|cv|nv|gmbh|ag|kg|ohg|ug|sa|sarl|sas|snc|spa|plc|llp|lp|as|oy|ab|pt|pte|pvt)\b/g, '');
    
    // Remove remaining special characters (periods, parentheses, etc.)
    normalized = normalized.replace(/[^a-z0-9\s]/g, '');
    
    // Normalize whitespace
    normalized = normalized.replace(/\s+/g, ' ').trim();

    return normalized;
  }

  filterProfiles() {
    if (!this.transactionRecord || !this.transactionRecord.counterPartyName) {
      this.filteredProfiles = this.transactionProfiles;
      this.applySearchFilter();
      return;
    }

    if (this.showAllProfiles) {
      this.filteredProfiles = this.transactionProfiles;
      this.applySearchFilter();
      return;
    }

    // Normalize transaction counterparty name
    const normalizedTransactionCp = this.normalizeName(this.transactionRecord.counterPartyName);
    const transactionCpWords = normalizedTransactionCp.split(/\s+/).filter(word => word.length > 0);
    
    this.filteredProfiles = this.transactionProfiles.filter(profile => {
      if (!profile.counterPartyName) {
        return false;
      }
      
      // Normalize profile counterparty name
      const normalizedProfileCp = this.normalizeName(profile.counterPartyName);
      const profileCpWords = normalizedProfileCp.split(/\s+/).filter(word => word.length > 0);
      
      // Check if at least one word from transaction counterparty matches a word in profile counterparty
      return transactionCpWords.some(txnWord => 
        txnWord.length > 2 && // Only match words longer than 2 characters
        profileCpWords.some(profileWord => 
          profileWord.includes(txnWord) || txnWord.includes(profileWord)
        )
      );
    });
    
    this.applySearchFilter();
  }

  applySearchFilter() {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.displayedProfiles = this.filteredProfiles.slice();
      this.orderRulesForThisPayment();
      return;
    }

    const searchLower = this.searchQuery.toLowerCase().trim();
    this.displayedProfiles = this.filteredProfiles.filter(profile => {
      const profileName = (profile.name || '').toLowerCase();
      const counterpartyName = (profile.counterPartyName || '').toLowerCase();
      return profileName.includes(searchLower) || counterpartyName.includes(searchLower);
    });
    this.orderRulesForThisPayment();
  }

  amountFitsProfile(profile: TransactionRule): boolean | null {
    const amount = this.paymentAmount();
    if (amount == null || profile.amountFrom == null || profile.amountTo == null) {
      return null;
    }
    const low = Math.min(Number(profile.amountFrom), Number(profile.amountTo));
    const high = Math.max(Number(profile.amountFrom), Number(profile.amountTo));
    return amount >= low && amount <= high;
  }

  private paymentAmount(): number | null {
    if (!this.transactionRecord || this.transactionRecord.amount == null) {
      return null;
    }
    const amount = Math.abs(Number(this.transactionRecord.amount));
    return isNaN(amount) ? null : amount;
  }

  private orderRulesForThisPayment() {
    this.displayedProfiles.sort((left, right) => {
      const leftFits = this.amountFitsProfile(left) === true ? 0 : 1;
      const rightFits = this.amountFitsProfile(right) === true ? 0 : 1;
      if (leftFits !== rightFits) {
        return leftFits - rightFits;
      }
      return this.rangeWidth(left) - this.rangeWidth(right);
    });
  }

  private rangeWidth(profile: TransactionRule): number {
    if (profile.amountFrom == null || profile.amountTo == null) {
      return Number.MAX_VALUE;
    }
    return Math.abs(Number(profile.amountTo) - Number(profile.amountFrom));
  }

  onSearchChange() {
    this.applySearchFilter();
  }

  toggleShowAllProfiles() {
    // showAllProfiles is already updated by ngModel, just filter the profiles
    this.filterProfiles();
  }

  selectProfile(profile: TransactionRule) {
    this.selectedProfile = profile;
    this.testResult = null;
  }

  testProfile() {
    if (!this.selectedProfile || !this.transactionRecord) {
      return;
    }

    this.isTesting = true;
    this.testResult = null;

    // Prepare test data with required fields
    const testData: any = {
      transactionId: this.transactionRecord.txnId,
      profileId: this.selectedProfile.id || null
    };

    // If profile has a counterparty account number, try to find the bank account ID
    if (this.selectedProfile.counterPartyId && this.selectedProfile.counterPartyAccountNumber) {
      this.financeService.getBankAccountsForEntity(this.selectedProfile.counterPartyId)
        .subscribe(accounts => {
          // Ensure accounts is an array before calling .find()
          let accountsArray: BankAccount[] = [];
          if (Array.isArray(accounts)) {
            accountsArray = accounts;
          } else if (accounts && typeof accounts === 'object') {
            // If it's a single object, wrap it in an array
            accountsArray = [accounts];
          }
          
          const matchingAccount = accountsArray.find((acc: BankAccount) => 
            acc && acc.accountNumber === this.selectedProfile.counterPartyAccountNumber
          );
          if (matchingAccount) {
            testData.bankAccountId = matchingAccount.accountId;
          }
          this.executeTest(testData);
        }, error => {
          console.error('Error loading bank accounts for test:', error);
          // Continue with test even if bank account lookup fails
          this.executeTest(testData);
        });
    } else {
      // No bank account to look up, proceed with test
      this.executeTest(testData);
    }
  }

  private executeTest(testData: any) {
    this.transactionMonitoringService.testTransactionRuleMatch(testData)
      .subscribe(
        response => {
          this.isTesting = false;
          if (response && response.arrResult) {
            const result = response.arrResult;
            this.testResult = {
              matches: result.matches === true,
              message: result.message || (result.matches ? 'Rule matches the transaction!' : 'Rule does not match the transaction.'),
              passedCriteria: result.passedCriteria || [],
              failedCriteria: result.failedCriteria || [],
              maxAmountTransactions: result.maxAmountTransactions || [],
              maxAmountTotal: result.maxAmountTotal,
              maxAmountExcludedDuplicates: result.maxAmountExcludedDuplicates || []
            };
          } else {
            this.testResult = {
              matches: false,
              message: 'Unable to test rule match.',
              passedCriteria: [],
              failedCriteria: []
            };
          }
        },
        error => {
          this.isTesting = false;
          console.error('Error testing rule match:', error);
          this.testResult = {
            matches: false,
            message: 'Error testing rule match: ' + (error.message || 'Unknown error')
          };
        }
      );
  }

  viewHistory(profile: TransactionRule, event: Event) {
    // Prevent the click from selecting the rule
    event.stopPropagation();
    
    const config = new MdDialogConfig();
    config.width = '900px';
    config.height = '80%';
    config.data = { 
      rule: profile,
      clientId: this.entityIdDetails ? this.entityIdDetails.clientId : null
    };
    
    const historyDialogRef = this.dialog.open(RuleHistoryDialogComponent, config);
    
    historyDialogRef.afterClosed().subscribe(result => {
      // If a version was restored, reload the profiles
      if (result && result.restored) {
        this.loadProfiles();
      }
    });
  }

  editProfile(profile: TransactionRule, event: Event) {
    // Prevent the click from selecting the rule
    event.stopPropagation();
    
    // Check if rule is FINAL - if so, show confirmation dialog
    // reviewStatus can be a string ('FINAL', 'DRAFT') or enum value
    const reviewStatusValue = profile.reviewStatus as any;
    const isFinal = reviewStatusValue === 'FINAL' || 
                    reviewStatusValue === ReviewStatus.FINAL ||
                    (typeof reviewStatusValue === 'string' && reviewStatusValue.toUpperCase() === 'FINAL');
    
    if (isFinal) {
      const confirmConfig = new MdDialogConfig();
      confirmConfig.width = '400px';
      const confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, confirmConfig);
      confirmDialogRef.componentInstance.title = 'Edit FINAL Rule';
      confirmDialogRef.componentInstance.message = 
        'Are you sure you want to edit this rule? A new DRAFT version of this rule will be created.';
      
      confirmDialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.openEditDialog(profile);
        }
      });
    } else {
      // DRAFT rule - open edit dialog directly
      this.openEditDialog(profile);
    }
  }

  private openEditDialog(profile: TransactionRule) {
    // Store the original profile ID and name to identify the new DRAFT version after editing
    const originalProfileId = profile.id;
    const originalProfileName = profile.name;
    const reviewStatusValue: any = profile.reviewStatus;
    const wasFinal = reviewStatusValue === ReviewStatus.FINAL || 
                     reviewStatusValue === 'FINAL' ||
                     (typeof reviewStatusValue === 'string' && reviewStatusValue.toUpperCase() === 'FINAL');
    
    // Create a minimal TxRuleData object for editing
    // Note: id is the right/obligation ID (not transaction rule ID), which we don't have
    const txRuleData: TxRuleData = {
      id: 0, // Right/obligation ID - not available when editing from test rule
      title: profile.name || '',
      counterPartyId: profile.counterPartyId || 0,
      counterPartyName: profile.counterPartyName || '',
      date: new Date(),
      agreementId: profile.agreementId || 0, // Use agreementId from rule if available
      transactionRule: profile
    };
    
    const config = new MdDialogConfig();
    const editDialogRef = this.dialog.open(AddTransactionRuleComponent, config);
    editDialogRef.componentInstance.selectedTxRule = txRuleData;
    editDialogRef.componentInstance.entityIdDetails = this.entityIdDetails;
    editDialogRef.componentInstance.agreementId = profile.agreementId || 0; // Use agreementId from rule if available
    editDialogRef.componentInstance.action = 'Edit';
    
    editDialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload rules after editing
        this.isLoadingProfiles = true;
        this.transactionProfileService.getTransactionRulesByClientId(this.entityIdDetails.clientId)
          .subscribe(profiles => {
            // Deduplicate rules by ID only (backend already handles versioning)
            // Different rules can legitimately have the same name, so we should NOT deduplicate by name
            const profilesMap = new Map<number, TransactionRule>();
            (profiles || []).forEach(p => {
              if (p.id) {
                const existing = profilesMap.get(p.id);
                if (!existing) {
                  profilesMap.set(p.id, p);
                }
              }
            });
            
            // Use all unique rules by ID (backend already returns only latest versions)
            this.transactionProfiles = Array.from(profilesMap.values());
            this.filterProfiles();
            this.isLoadingProfiles = false;
            
            // Find and select the updated rule
            // If we edited a FINAL rule, find the new DRAFT version (same name, different ID, DRAFT status)
            // If we edited a DRAFT rule, find the updated DRAFT version (same ID or same name with DRAFT status)
            let updatedProfile: TransactionRule = null;
            
            if (wasFinal) {
              // Editing a FINAL rule creates a new DRAFT version
              // Find the DRAFT version with the same name (should be the one with highest ID)
              updatedProfile = this.transactionProfiles.find(p => {
                const sameName = (p.name || '').trim() === (originalProfileName || '').trim();
                const pReviewStatus: any = p.reviewStatus;
                const isDraft = pReviewStatus === ReviewStatus.DRAFT || 
                               pReviewStatus === 'DRAFT' ||
                               (typeof pReviewStatus === 'string' && pReviewStatus.toUpperCase() === 'DRAFT');
                const differentId = p.id !== originalProfileId;
                return sameName && isDraft && differentId;
              });
              
              // If not found by name, try to find the DRAFT version with highest ID for this name
              if (!updatedProfile) {
                const matchingProfiles = this.transactionProfiles.filter(p => {
                  const sameName = (p.name || '').trim() === (originalProfileName || '').trim();
                  const pReviewStatus: any = p.reviewStatus;
                  const isDraft = pReviewStatus === ReviewStatus.DRAFT || 
                                 pReviewStatus === 'DRAFT' ||
                                 (typeof pReviewStatus === 'string' && pReviewStatus.toUpperCase() === 'DRAFT');
                  return sameName && isDraft;
                });
                if (matchingProfiles.length > 0) {
                  // Get the one with highest ID (latest version)
                  updatedProfile = matchingProfiles.reduce((latest, current) => {
                    return (current.id && latest.id && current.id > latest.id) ? current : latest;
                  });
                }
              }
            } else {
              // Editing a DRAFT rule updates it in place (same ID)
              updatedProfile = this.transactionProfiles.find(p => p.id === originalProfileId);
              
              // If not found by ID, try to find by name with DRAFT status
              if (!updatedProfile) {
                updatedProfile = this.transactionProfiles.find(p => {
                  const sameName = (p.name || '').trim() === (originalProfileName || '').trim();
                  const pReviewStatus: any = p.reviewStatus;
                  const isDraft = pReviewStatus === ReviewStatus.DRAFT || 
                                 pReviewStatus === 'DRAFT' ||
                                 (typeof pReviewStatus === 'string' && pReviewStatus.toUpperCase() === 'DRAFT');
                  return sameName && isDraft;
                });
              }
            }
            
            // Update selectedProfile to the new/updated rule
            if (updatedProfile) {
              this.selectedProfile = updatedProfile;
              // Clear test result since we're now testing a different/updated rule
              this.testResult = null;
              
              // Make sure the updated profile is visible in the displayed list
              if (!this.displayedProfiles.find(p => p.id === updatedProfile.id)) {
                // If it's not visible, show all profiles to make it visible
                this.showAllProfiles = true;
                this.filterProfiles();
              }
            } else {
              // If we can't find the updated profile, at least clear the selection and test result
              console.warn('Could not find updated profile after editing. Original ID:', originalProfileId, 'Name:', originalProfileName);
              this.selectedProfile = null;
              this.testResult = null;
            }
          }, error => {
            console.error('Error loading profiles after edit:', error);
            this.isLoadingProfiles = false;
          });
      }
    });
  }

  openCreateProfileDialog() {
    // Store current rule IDs to identify the newly created one
    const existingProfileIds = new Set(this.transactionProfiles.map(p => p.id));
    
    // Create a TxRuleData object with pre-filled data from transaction record
    const txRuleData: TxRuleData = {
      id: 0, // New rule, no right/obligation ID yet
      title: '', // User will enter the rule name
      counterPartyId: 0, // User will need to select counterparty
      counterPartyName: (this.transactionRecord && this.transactionRecord.counterPartyName) || '',
      date: (this.transactionRecord && this.transactionRecord.txnDate) || new Date(),
      agreementId: 0, // Will be created when rule is saved
      transactionRule: null // New rule, no existing rule
    };
    
    const config = new MdDialogConfig();
    const createDialogRef = this.dialog.open(AddTransactionRuleComponent, config);
    createDialogRef.componentInstance.selectedTxRule = txRuleData;
    createDialogRef.componentInstance.entityIdDetails = this.entityIdDetails;
    createDialogRef.componentInstance.agreementId = 0; // New agreement will be created
    // Don't set action to 'Edit', so it will create a new rule
    
    createDialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload rules after creating
        // The loadProfiles method will call filterProfiles when done
        this.isLoadingProfiles = true;
        this.transactionProfileService.getTransactionRulesByClientId(this.entityIdDetails.clientId)
          .subscribe(profiles => {
            this.transactionProfiles = profiles || [];
            
            // Find the newly created rule (one that wasn't in the existing list)
            const newProfile = this.transactionProfiles.find(p => !existingProfileIds.has(p.id));
            
            // Filter rules first
            this.filterProfiles();
            this.isLoadingProfiles = false;
            
            // If we found a new rule, select it
            if (newProfile) {
              // Make sure it's visible - show all rules if needed
              if (!this.displayedProfiles.find(p => p.id === newProfile.id)) {
                this.showAllProfiles = true;
                this.filterProfiles();
              }
              
              // Select the new rule
              this.selectedProfile = newProfile;
              this.testResult = null;
              
              // Scroll to the selected rule
              setTimeout(() => {
                const profileElements = document.querySelectorAll('.profile-item');
                Array.prototype.slice.call(profileElements).forEach((el: HTMLElement) => {
                  if (el.textContent && el.textContent.includes(newProfile.name || '')) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('selected');
                  }
                });
              }, 100);
            }
          }, error => {
            console.error('Error loading transaction rules:', error);
            this.transactionProfiles = [];
            this.filteredProfiles = [];
            this.displayedProfiles = [];
            this.isLoadingProfiles = false;
          });
      }
    });
  }

  makeProfileFinal() {
    if (!this.selectedProfile || !this.selectedProfile.id) {
      return;
    }

    // Check if rule is DRAFT
    const reviewStatusValue = this.selectedProfile.reviewStatus as any;
    const isDraft = reviewStatusValue === 'DRAFT' || 
                    reviewStatusValue === ReviewStatus.DRAFT ||
                    (typeof reviewStatusValue === 'string' && reviewStatusValue.toUpperCase() === 'DRAFT');

    if (!isDraft) {
      console.warn('Rule is not DRAFT, cannot make it FINAL');
      return;
    }

    // Create a TransactionRule with status set to FINAL
    // Manually copy properties to avoid spread operator issues with TypeScript
    const updatedRule: TransactionRule = {
      id: this.selectedProfile.id,
      accountNo: this.selectedProfile.accountNo,
      name: this.selectedProfile.name,
      txnDescription: this.selectedProfile.txnDescription,
      cashFlow: this.selectedProfile.cashFlow,
      amountFrom: this.selectedProfile.amountFrom,
      amountTo: this.selectedProfile.amountTo,
      maxAmount: this.selectedProfile.maxAmount,
      maxAmountPeriod: this.selectedProfile.maxAmountPeriod,
      maxAmountPeriodName: this.selectedProfile.maxAmountPeriodName,
      maxAmountPeriodFrom: this.selectedProfile.maxAmountPeriodFrom,
      maxAmountPeriodTo: this.selectedProfile.maxAmountPeriodTo,
      frequency: this.selectedProfile.frequency,
      frequencyPeriod: this.selectedProfile.frequencyPeriod,
      frequencyPeriodName: this.selectedProfile.frequencyPeriodName,
      frequencyPeriodFrom: this.selectedProfile.frequencyPeriodFrom,
      frequencyPeriodTo: this.selectedProfile.frequencyPeriodTo,
      counterPartyId: this.selectedProfile.counterPartyId,
      counterPartyName: this.selectedProfile.counterPartyName,
      counterPartyAccountNumber: this.selectedProfile.counterPartyAccountNumber,
      counterPartyAccounts: this.selectedProfile.counterPartyAccounts,
      originCountry: this.selectedProfile.originCountry,
      cashFlowName: this.selectedProfile.cashFlowName,
      originCountryName: this.selectedProfile.originCountryName,
      reviewStatus: ReviewStatus.FINAL,
      agreementId: this.selectedProfile.agreementId
    };

    // Call the save endpoint to update the rule status to FINAL
    this.transactionProfileService.updateTransactionRuleStatus(
      this.entityIdDetails.clientId,
      updatedRule
    ).subscribe(
      response => {
        if (response && response.flagResult) {
          // Reload rules to reflect the updated status
          this.loadProfiles();
          // Update selected rule status
          this.selectedProfile.reviewStatus = ReviewStatus.FINAL;
        }
      },
      error => {
        console.error('Error making rule FINAL:', error);
        alert('Error making rule FINAL: ' + (error.message || 'Unknown error'));
      }
    );
  }

  close() {
    this.dialogRef.close();
  }
}




// WEBPACK FOOTER //
// ./src/app/entities/transaction-monitoring/test-profile-wizard/test-profile-wizard.component.ts